import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { Button, Input, Card, Logo } from '@/components/ui';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const { t } = useTranslation();
  const { resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await resetPassword(email);
      setIsSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.failedToSendResetEmail'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-900 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <Logo size="md" />
        </Link>

        <Card variant="elevated" padding="lg">
          {isSuccess ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-dark-800 dark:text-white mb-2">{t('auth.checkYourEmail')}</h1>
              <p className="text-gray-500 dark:text-gray-400 mb-6">
                {t('auth.resetEmailSent')}{' '}
                <span className="font-medium text-dark-800 dark:text-white">{email}</span>
              </p>
              <p className="text-sm text-gray-400 mb-6">
                {t('auth.didntReceiveEmail')}
              </p>
              <Button
                variant="ghost"
                size="md"
                onClick={() => {
                  setIsSuccess(false);
                  setEmail('');
                }}
              >
                {t('auth.tryAnotherEmail')}
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h1 className="text-2xl font-bold text-dark-800 dark:text-white mb-2">{t('auth.resetYourPassword')}</h1>
                <p className="text-gray-500 dark:text-gray-400">
                  {t('auth.resetDescription')}
                </p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label={t('auth.email')}
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  leftIcon={<Mail className="w-5 h-5" />}
                  required
                  autoComplete="email"
                />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  isLoading={isLoading}
                >
                  {t('auth.sendResetLink')}
                </Button>
              </form>
            </>
          )}
        </Card>

        <Link
          to="/login"
          className="mt-6 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 hover:text-dark-800 dark:hover:text-white transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('auth.backToSignIn')}
        </Link>
      </div>
    </div>
  );
};

export default ForgotPassword;
